import { useState, useRef, useEffect } from 'react'
import type { DragEvent, ReactNode } from 'react'
import type { WizardState } from './types'
import { inputStyle } from './ui'
import { apiFetch } from '../../../lib/api-client'
import { regimeClauses } from './regimeClauses'
import type { RentRegime } from './regimeClauses'
import { reconcileRegime, noteManualChange } from './regimeSync'
const BLUE = '#4f46e5'

// ── Types ─────────────────────────────────────────────────────────────────────

type ClauseItem =
  | { kind: 'smart'; id: string }
  | { kind: 'custom'; localId: string; text: string }

interface SmartClause { id: string; title: string; text: string; category: string }

// Saved reusable clause templates (#15) — personal ("Smartvilkår") or company ("Selskabsvilkår").
interface SavedVilkaar { id: string; scope: 'personal' | 'company'; title: string | null; content: string }

// ── Smartvilkår library ───────────────────────────────────────────────────────

const SMARTVILKAAR: SmartClause[] = [
  // ── Boligtype & husleje ──────────────────────────────────────────────────────
  { id: 'sv-01', category: 'Boligtype & husleje', title: 'Røgfri beboelse', text: 'Lejemålet må ikke uden udlejers skriftlige samtykke anvendes til andet end røgfri helårsbeboelse for lejer med husstand. Rygning kan sive igennem etageadskillelsen og generer de øvrige lejere, ligesom rygning skader tapet, væv, lofter og træværk og gør omkostningerne til istandsættelse i forbindelse med lejers fraflytning gevaldigt højere. Rygning i det lejede beses som kontraktstridig brug af det lejede.' },
  { id: 'sv-02', category: 'Boligtype & husleje', title: 'Ejendom ibrugtaget efter 1991 (ureguleret)', text: 'Lejemålet er beliggende i en ejendom, der er taget i brug efter den 31. december 1991 og er dermed omfattet af lejelovens § 54, stk. 1, nr. 1. Lejelovens regler om lejeforhøjelse- og nedsættelse efter det lejedes værdi, jf. lejelovens §§ 42-45, skal ikke gælde for denne lejeaftale.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-03', category: 'Boligtype & husleje', title: 'Forhenværende erhverv (ureguleret)', text: 'Lejemålet benyttedes den 31. december 1991 lovligt udelukkende til erhvervsformål og er dermed omfattet af lejelovens § 54, stk. 1, nr. 2. Lejelovens regler om lejeforhøjelse- og nedsættelse efter det lejedes værdi, jf. lejelovens §§ 42-45, skal ikke gælde for denne lejeaftale.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-04', category: 'Boligtype & husleje', title: 'Nyindrettet tagetage (ureguleret)', text: 'Lejemålet er beliggende i en nyindrettet tagetage, som den 1. september 2002 ikke var benyttet til eller registreret som beboelse, og er dermed omfattet af lejelovens § 54, stk. 1, nr. 3. Lejelovens regler om lejeforhøjelse- og nedsættelse efter det lejedes værdi, jf. lejelovens §§ 42-45, skal ikke gælde for denne lejeaftale.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-05', category: 'Boligtype & husleje', title: 'Nypåbygget etage (ureguleret)', text: 'Lejemålet er beliggende i en nypåbygget etage, hvortil der er givet byggetilladelse efter den 1. juli 2004 og er dermed omfattet af lejelovens § 54, stk. 1, nr. 3. Lejelovens regler om lejeforhøjelse- og nedsættelse efter det lejedes værdi, jf. lejelovens §§ 42-45, skal ikke gælde for denne lejeaftale.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-06', category: 'Boligtype & husleje', title: 'Ejendom ibrugtaget efter 1991 (reguleret)', text: 'Lejemålet er beliggende i en ejendom, der er taget i brug efter den 31. december 1991 og er derfor omfattet af lejelovens § 54, stk. 1, nr. 1. Lejelovens regler om omkostningsbestemt leje skal ikke gælde for denne lejeaftale, jf. lejelovens § 41, stk. 2.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-07', category: 'Boligtype & husleje', title: 'Forhenværende erhverv (reguleret)', text: 'Lejemålet benyttedes den 31. december 1991 lovligt udelukkende til erhvervsformål og er derfor omfattet lejelovens § 54, stk. 1, nr. 2. Lejelovens regler om omkostningsbestemt leje skal ikke gælde for denne lejeaftale, jf. lejelovens § 41, stk. 2.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-08', category: 'Boligtype & husleje', title: 'Nyindrettet tagetage (reguleret)', text: 'Lejemålet er beliggende i en nyindrettet tagetage, som den 1. september 2002 ikke var benyttet til eller registreret som beboelse og er dermed omfattet af lejelovens § 54, stk. 1, nr. 3. Lejelovens regler om omkostningsbestemt leje skal ikke gælde for denne lejeaftale, jf. lejelovens § 41, stk. 2.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-09', category: 'Boligtype & husleje', title: 'Nypåbygget etage (reguleret)', text: 'Lejemålet er beliggende i en nypåbygget etage, hvortil der er givet byggetilladelse efter den 1. juli 2004 og er dermed omfattet af lejelovens § 54, stk. 1, nr. 3. Lejelovens regler om omkostningsbestemt leje skal ikke gælde for denne lejeaftale, jf. lejelovens § 41, stk. 2.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  { id: 'sv-10', category: 'Boligtype & husleje', title: 'Husorden', text: 'Den aktuelle husorden kan på hvilket som helst tidspunkt og uden yderligere varsel modificeres af udlejer ved skriftlig fremsendelse af eftergået version til alle lejere. Ifald der er en beboerrepræsentation i ejendommen, kan et beboermøde godkende en husorden. En sådan husorden kan dog ikke ændre på kontraktens vilkår.' },
  { id: 'sv-11', category: 'Boligtype & husleje', title: 'Forhenværende erhvervslejemål (reguleret)', text: 'Lejemålet var den 31. december 1991 indrettet udelukkende til erhvervsformål og er derfor omfattet lejelovens § 54, stk. 1, nr. 2. Lejelovens regler om omkostningsbestemt leje skal ikke gælde for denne lejeaftale, jf. lejelovens § 41, stk. 2.\n\nI stedet for har parterne aftalt, at den gældende leje ekskl. aconto betalinger reguleres efter nettoprisindekset, jf. lejelovens § 54, stk. 3. Regulering sker ikke, hvis nettoprisindekset er negativt.\n\nForuden regulering efter nettoprisindekset gælder lejelovgivningens almindelige regler om lejeregulering på grund af ændringer i skatter og afgifter, jf. lejelovens §§ 46-48.' },
  // ── Parterne & det lejede ─────────────────────────────────────────────────────
  { id: 'sv-12', category: 'Parterne & det lejede', title: 'Synsrapport udleveres digitalt', text: 'Idet der er aftalt digital kommunikation imellem lejer og udlejer, vil synsrapporter i forbindelse med indflytnings- og flyttesyn blive udleveret via e-mail. Lejer skal derfor medbringe en telefon eller anden enhed, hvorfra det er muligt at læse e-mails og kvittere for modtagelsen af synsrapporter.\n\nVed lejekontraktens indgåelse er lejers e-mailadresse registreret som angivet i § 3. Dokumenter afsendt til den oplyste e-mailadresse anses for modtaget af den anden part.' },
  { id: 'sv-13', category: 'Parterne & det lejede', title: 'Indeklimavejledning', text: 'Lejer er forpligtet til at følge Sundhedsstyrelsens anbefalinger om at skabe et sundt indeklima, bl.a. for at undgå fugt- og skimmelproblemer: Det lejede opvarmes til min. 18 grader døgnet rundt, der udluftes flere gange om dagen i 5-10 minutter og emhætte benyttes ved madlavning. Lejer skal især være opmærksom på indeklimaet, hvis der tørres tøj i det lejede. Møbler og andet inventar må ikke anbringes helt op af kolde ydrevægge.' },
  // ── Lejemålets begyndelse & ophør ─────────────────────────────────────────────
  { id: 'sv-14', category: 'Lejemålets begyndelse & ophør', title: 'Tidsbegrænset leje med begrundelse', text: 'Lejemålet er tidsbegrænset og ophører derfor uden opsigelse på den i § 2 angivne dato. Tidsbegrænsningen er begrundet i [Her anføres de forhold hos udlejer, der kan begrunde tidsbegrænsningen].' },
  { id: 'sv-15', category: 'Lejemålets begyndelse & ophør', title: 'Lejemålet er en ejerlejlighed', text: 'Inden lejemålets indgåelse er lejer gjort bekendt med, at lejemålet er en ejerlejlighed, som udlejer tidligere har beboet, og at udlejer kan opsige lejemålet efter lejelovens § 171, stk. 1, nr. 1, jf. § 172, nr. 4.' },
  { id: 'sv-16', category: 'Lejemålets begyndelse & ophør', title: 'Uopsigeligt tidsbegrænset leje', text: 'Lejemålet er tidsbegrænset og ophører derfor uden opsigelse på den i § 2 angivne dato. Lejemålet er uopsigeligt for begge parter i lejeperioden.' },
  { id: 'sv-17', category: 'Lejemålets begyndelse & ophør', title: 'Periodevist uopsigeligt lejemål', text: 'Lejemålet er uopsigeligt for lejer i [x antal] måneder fra lejeforholdets begyndelse, jf. § 2.' },
  { id: 'sv-18', category: 'Lejemålets begyndelse & ophør', title: 'Indflytning første hverdag', text: 'Lejeforholdet begynder på den i § 2 angivne dato og lejemålet overdrages til lejer samme dato som afholdelse af indflytningssyn. Er tidspunktet for lejeforholdets begyndelse en lørdag, søndag eller helligdag, udskydes tidspunktet for lejemålets overdragelse og afholdelse af indflytningssyn til førstkommende hverdag. Overdragelsen forudsætter, at lejer har indbetalt de i § 4 angivne beløb.' },
  { id: 'sv-19', category: 'Lejemålets begyndelse & ophør', title: 'Halvt dobbelthus — bebos også af udlejer', text: 'Inden lejemålets indgåelse er lejer gjort bekendt med, at lejemålet er en beboelseslejlighed i et hus med to beboelseslejligheder, hvoraf ejer af lejemålet bebor det ene.' },
  { id: 'sv-20', category: 'Lejemålets begyndelse & ophør', title: 'Tidsbegrænset leje', text: 'Lejemålet er tidsbegrænset og ophører derfor uden opsigelse på den i § 2 angivne dato. I lejeperioden kan lejemålet opsiges efter reglerne i lejeloven.' },
  { id: 'sv-21', category: 'Lejemålets begyndelse & ophør', title: 'Fraflytning 10 hverdage før ophør', text: 'Lejemålet skal fraflyttes senest 10 hverdage før lejemålets ophør, således lejemålet kan blive sat i stand. Lejer betaler leje i istandsættelsesperioden.' },
  { id: 'sv-22', category: 'Lejemålets begyndelse & ophør', title: 'Fraflytning 14 dage før ophør', text: 'Ved lejemålets ophør skal det lejede afleveres i samme stand som ved indflytningen. Lejemålet skal fraflyttes senest 14 dage før lejemålets ophør, med henblik på istandsættelse. Lejer betaler leje i istandsættelsesperioden. Eventuelle nødvendige istandsættelsesarbejder i forbindelse med lejers fraflytning i lejemålet gennemføres ved udlejers foranstaltning, efter lejeren er fraflyttet og for lejerens regning.' },
  // ── Lejemålets forsyninger ────────────────────────────────────────────────────
  { id: 'sv-23', category: 'Lejemålets forsyninger', title: 'Tilmelding varmeselskab', text: 'Udlejer leverer ikke varme og varmt vand til lejemålet, og lejer er derfor forpligtet til senest 3 hverdage inden lejeforholdets begyndelse at registrere sig som selvstændig kunde/forbruger på lejemålets adresse hos et forsyningsselskab (fjernvarmeværk el. lignende) og afregner derfor betaling direkte med forsyningsselskabet. Lejer er ligeså forpligtet til senest 3 hverdage efter lejeforholdets ophør at afmelde sig som kunde/forbruger på adressen. Lejer er forpligtet til at sørge for forsyningen med varme og vand ikke afbrydes på grund af lejers forhold, herunder ved manglende betaling til forsyningsselskabet. Lejer er forpligtet til at være tilmeldt som kunde/forbruger hos forsyningsselskabet fra lejeforholdets begyndelse til lejeforholdets ophør.' },
  { id: 'sv-24', category: 'Lejemålets forsyninger', title: 'Flytteaflæsningsgebyr', text: 'Gebyrer i forbindelse med flytte- og årsaflæsninger af varme, el, gas og vand, hvor opsatte målere er tilknyttet, opkræves hos lejer i henhold til forsyningsselskabernes aktuelle takster.' },
  { id: 'sv-25', category: 'Lejemålets forsyninger', title: 'Manuel el-tilmeldingspligt', text: 'Udlejer leverer ikke el til lejemålet, og lejer er derfor forpligtet til senest 3 hverdage inden lejeforholdets begyndelse at registrere sig som selvstændig kunde/forbruger på lejemålets adresse hos et elselskab og afregner derfor betaling direkte med elselskabet. Lejer er ligeså forpligtet til senest 3 hverdage efter lejeforholdets ophør at afmelde sig som kunde/forbruger på adressen.' },
  { id: 'sv-26', category: 'Lejemålets forsyninger', title: 'Tilmelding vandværk', text: 'Udlejer leverer ikke vand til lejemålet, og lejer er derfor forpligtet til senest 3 hverdage inden lejeforholdets begyndelse at registrere sig som selvstændig kunde/forbruger på lejemålets adresse hos et vandværk. Lejer er ligeså forpligtet til senest 3 hverdage efter lejeforholdets ophør at afmelde sig som kunde/forbruger på adressen.' },
  { id: 'sv-27', category: 'Lejemålets forsyninger', title: 'Ændringer i TV-pakke', text: 'Udlejer leverer tv-pakke til lejemålet, jf. § 6. Eventuelle justeringer i abonnement eller valg af nye kanaler skal godkendes skriftligt af udlejer inden indgåelse af ny aftale. Såfremt eventuelle justeringer foretages, vil udgiften hertil tilgå lejer.' },
  { id: 'sv-28', category: 'Lejemålets forsyninger', title: 'Ændringer i internetabonnement', text: 'Udlejer leverer internet til lejemålet, jf. § 6. Eventuelle justeringer i abonnement eller hastigheder skal godkendes skriftligt af udlejer inden indgåelse af ny aftale. Såfremt eventuelle justeringer foretages, vil udgiften hertil tilgå lejer.' },
  { id: 'sv-29', category: 'Lejemålets forsyninger', title: 'Forbrugsudgifter inkl. i leje', text: 'Vand, varme og el er inkl. i lejen, og lejer skal være sædvanlig betænkelig med sit forbrug af varme, varmt og koldt vand, el og øvrigt inkl. forbrug.' },
  { id: 'sv-30', category: 'Lejemålets forsyninger', title: 'Fuldmagt til tilmelding forsyning', text: 'Lejer giver fuldmagt til at udlejer kan tilmelde lejer som aftager af varme, vand og el. Såfremt lejer, efter meddelelse herom har fundet sted, ønsker andet forsyningsvirksomhed, skal lejer selv sørge for at meddele rettidig tilflytning til den af lejer valgte forsyningsvirksomhed.' },
  // ── Lejemålets stand ved ind- og fraflytning ──────────────────────────────────
  { id: 'sv-31', category: 'Lejemålets stand ved ind- og fraflytning', title: 'Indkaldelse til indflytningssyn', text: 'Lejer indkaldes hermed til indflytningssyn på lejemålets adresse på den i § 2 angivne dato. Her vil der samtidig blive overdraget nøgler til lejemålet.' },
  { id: 'sv-32', category: 'Lejemålets stand ved ind- og fraflytning', title: 'Indkaldelse til syn — dato aftales', text: 'Lejer indkaldes hermed til indflytningssyn på lejemålets adresse. Datoen aftales på et senere tidspunkt. Her vil der samtidig blive overdraget nøgler til lejemålet.' },
  { id: 'sv-33', category: 'Lejemålets stand ved ind- og fraflytning', title: 'Lejemålet overtages nymalet', text: 'Lejemålet overtages ved indflytningen som nymalet på samtlige malbare flader og med nylakerede gulve i hele lejemålet. Alle installationer er i god og brugbar tilstand. Der er ingen mangler i lejemålet.' },
  { id: 'sv-34', category: 'Lejemålets stand ved ind- og fraflytning', title: 'Lejemålet overtages velvedligeholdt', text: 'Lejemålet overtages ved indflytningen ikke som nymalet. Lejemålet fremtræder imidlertid velvedligeholdt med hvidmalede vægge, hvidmalet træværk og lakerede gulve uden større ridser eller præg. Alle målbare flader fremtræder som nymalede. Alle ruder er hele, og hårde hvidevarer og installationer er i god og brugbar tilstand.' },
  { id: 'sv-35', category: 'Lejemålets stand ved ind- og fraflytning', title: 'Mangler ved lejemål', text: 'Såfremt lejer ved lejers overtagelse af lejemålet konstaterer mangler ved lejemålet, skal lejer senest 14 dage efter lejeforholdets begyndelse skriftligt meddele udlejer, at lejer vil gøre manglen gældende.' },
  { id: 'sv-36', category: 'Lejemålets stand ved ind- og fraflytning', title: 'Indkaldelse til syn med kort varsel', text: 'Lejer indkaldes hermed til indflytningssyn på lejemålets adresse d. [angiv dato]. Her vil der samtidig blive overdraget nøgler til lejemålet. Lejer godtager det korte indkaldelsesvarsel, da lejer ønsker hurtig overtagelse af lejemålet.' },
  // ── Vedligeholdelse ───────────────────────────────────────────────────────────
  { id: 'sv-37', category: 'Vedligeholdelse', title: 'Begrænset indretningsmuligheder', text: 'Uden udlejers skriftlige samtykke, må lejer ikke ændre farven på lejemålets vægge, gulve, lofter, træværk, vinduer, døre eller metal. Lejer skal orientere sig om placering af ledninger i vægge inden boring. Det er ikke tilladt at bore huller i døre, klinker, fuger, skabe, vinduesrammer, fliser eller bordplader. Dette er misligholdelse og vil medføre omfattende istandsættelse for lejerens regning.' },
  { id: 'sv-38', category: 'Vedligeholdelse', title: 'Udvendig vedligeholdelse', text: 'Den udvendige vedligeholdelse af lejemålet samt tilhørende arealer påhviler lejer. Tagrender skal være rensede og funktionsdygtige uden lækager. Have skal fremstå velholdt med kortklippet græs, klippede hække, ukrudtsfrie bede, algebehandlede sten og tag efter fornødenhed. Indkørsel skal fremstå revet, som efter behov ligeså luges for ukrudt. Udvendige vægge, døre, sternbrædder, dørkarme, vinduesrammer og -karme, udhæng og stakit skal males og opretholdes efter behov. Lejer skal endvidere vedligeholde og forny ruder, samt holde udvendige vandhaner, gashaner og el-afbrydere forsvarligt vedlige. Opfylder lejeren ikke pligten inden 8 dage efter, at udlejeren har bedt lejer om det, kan udlejer istandsætte for lejerens regning.' },
  { id: 'sv-39', category: 'Vedligeholdelse', title: 'Ualmindelig brug', text: 'Hvis lejers brug påvirker ejendommens eller lejemålets vedligeholdelsesbehov ekstraordinært, eksempelvis ved rygning, sodpåvirkning og tilsvarende, må lejer påregne hyppigere og i visse tilfælde præventiv vedligeholdelse.' },
  { id: 'sv-40', category: 'Vedligeholdelse', title: 'Forøget vedligeholdelse', text: 'Ud over den i § 8 angivne vedligeholdelse, som påhviler lejer, skal lejer vedligeholde og forny ruder og sørge for at indvendige vandhaner, gashaner og el-afbrydere forsvarligt holdes ved lige. Lejer skal desuden vedligeholde wc-kummer, cisterner, badekar, vaskekummer, komfurer, køleskabe, vaskemaskiner og lignende installationer. Lejeren skal derudover rense eventuelt tilstoppet afløb fra køkkenvask, håndvask og toilet. Opfylder lejeren ikke pligten inden 8 dage efter, at udlejeren har bedt lejer om det, kan udlejer istandsætte for lejerens regning.' },
  { id: 'sv-41', category: 'Vedligeholdelse', title: 'Begrænset valg af maling', text: 'Ønsker lejer at male lejemålet må der kun males i [farve] nuancer og udelukkende med dækkende maling af god kvalitet. Det er ikke tilladt at anvende strukturmaling og mineralsk puds i lejemålet.' },
  { id: 'sv-42', category: 'Vedligeholdelse', title: 'Udlejers egne håndværkere', text: 'Eventuelle nødvendige istandsættelsesarbejder i forbindelse med lejers fraflytning af lejemålet gennemføres ved udlejers foranstaltning ved udlejers håndværkere, for lejerens regning. Hvis det konstateres, at lejemålet skal males, og lejer ikke ønsker lejemålet malet på udlejers foranledning, er lejer berettiget til at antage et andet firma til at gennemføre malingen af lejemålet. Det er dog en betingelse, at malerarbejdet udføres af et momsregistreret firma, og faktura skal kunne fremvises ved fraflytningssynet.\n\nSkal udlejer udføre istandsættelsesarbejder, vil afregning af depositum først ske, når disse istandsættelsesarbejder er udført og fakturaer modtaget fra håndværkere.' },
  { id: 'sv-43', category: 'Vedligeholdelse', title: 'Vedligeholdelse af have', text: 'Lejer skal passe og pleje den til lejemålet hørende have og udeareal, således have og udeareal til enhver tid fremstår velholdt og velplejet. Lejer skal blandt andet slå græs, klippe hæk, fjerne ukrudt, passe bede og rydde sne og salte efter behov. Fremstår have og udeareal ikke velholdt og velplejet ved fraflytningssynet, vil arbejder blive foranstaltet af udlejer for lejers regning.' },
  // ── Husdyr ────────────────────────────────────────────────────────────────────
  { id: 'sv-44', category: 'Husdyr', title: 'Specialtilladelse til husdyr', text: 'Uagtet forbuddet mod at holde husdyr, er der givet samtykke til, at lejeren kan beholde sin nuværende [hund/kat af racen x, øretatovering x]. Dette samtykke kan af udlejer med 6 måneders varsel tilbagekaldes, hvis husdyret efter udlejers vurdering, vurderes til gene i form af lugt eller lyd, eller hvis dyret beskadiger lejemålet.' },
  { id: 'sv-45', category: 'Husdyr', title: 'Tilladt at holde fugl, fisk, gnaver', text: 'Lejer må i lejemålet kun holde stuefugle, gnavere i bur og akvariefisk.' },
]

const CATEGORIES = [
  'Boligtype & husleje',
  'Parterne & det lejede',
  'Lejemålets begyndelse & ophør',
  'Lejemålets forsyninger',
  'Lejemålets stand ved ind- og fraflytning',
  'Vedligeholdelse',
  'Husdyr',
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function computeSpecialTerms(items: ClauseItem[], overrides: Record<string, string> = {}): string {
  let stk = 0
  return items
    .map(item => {
      stk++
      if (item.kind === 'smart') {
        const id = (item as { kind: 'smart'; id: string }).id
        const clause = SMARTVILKAAR.find(c => c.id === id)
        if (!clause) return ''
        const text = overrides[id] ?? clause.text
        return `§11, stk. ${stk} ${clause.title}\n${text}`
      }
      return (item as { kind: 'custom'; localId: string; text: string }).text
    })
    .filter(Boolean)
    .join('\n\n')
}

// Bruges af wizardens state-opdatering til at genopbygge §11-teksten, når en regime-ændring
// på Generelt-trinnet har ændret de valgte klausuler mens Vilkår-trinnet ikke er åbent.
// Ren hjælpefunktion; klausulbiblioteket ligger i denne fil, så den eksporteres herfra.
// eslint-disable-next-line react-refresh/only-export-components
export function specialTermsFor(
  smartIds: string[],
  customTexts: string[],
  overrides: Record<string, string> = {},
): string {
  const items: ClauseItem[] = [
    ...smartIds.map(id => ({ kind: 'smart', id }) as ClauseItem),
    ...customTexts.filter(t => t.trim()).map((text, i) => ({ kind: 'custom', localId: `s-${i}`, text }) as ClauseItem),
  ]
  return computeSpecialTerms(items, overrides)
}

function initItems(state: WizardState): ClauseItem[] {
  const smart: ClauseItem[] = (state.smartvilkaar_active ?? []).map(id => ({ kind: 'smart', id }))
  const custom: ClauseItem[] = state.special_terms_clauses
    .filter(t => t.trim())
    .map((text, i) => ({ kind: 'custom', localId: `init-${i}`, text }))
  return [...smart, ...custom]
}

function toState(items: ClauseItem[], overrides: Record<string, string> = {}): Partial<WizardState> {
  const smartIds = items.filter(i => i.kind === 'smart').map(i => (i as { kind: 'smart'; id: string }).id)
  const customTexts = items.filter(i => i.kind === 'custom').map(i => (i as { kind: 'custom'; localId: string; text: string }).text)
  return {
    smartvilkaar_active: smartIds,
    special_terms_clauses: customTexts.length > 0 ? customTexts : [''],
    special_terms: computeSpecialTerms(items, overrides),
    smart_overrides: overrides,
    special_terms_review: false,
    special_terms_edited: false,
  }
}

// ── Regime → klausul-forslag ──────────────────────────────────────────────────
// Mappingen (regime × grundlag → klausul-id) kommer fra regimeClauses.ts, som spejler
// docs/canonical-facts.yaml. Wizarden FORESLÅR kun: klausulen forvælges, når kombinationen
// af fri leje + grundlag + regime ændres til en afklaret værdi, og landlord kan altid
// fravælge den. Klausulteksterne ændres ikke.

const GRUNDLAG_LABEL: Record<string, string> = {
  post1991: 'Ejendom ibrugtaget efter 1991 (§ 54, stk. 1, nr. 1)',
  erhverv1991: 'Forhenværende erhverv (§ 54, stk. 1, nr. 2)',
  tagetage: 'Nyindrettet tagetage (§ 54, stk. 1, nr. 3)',
}

const REGIME_LABEL: Record<'ureguleret' | 'reguleret', string> = {
  ureguleret: 'Ureguleret – fravalg af lejelovens §§ 42-45',
  reguleret: 'Reguleret – fravalg af lejelovens § 41, stk. 2 (omkostningsbestemt leje)',
}

// Afstemningen af forvalgte klausuler ligger i regimeSync.ts og køres både fra wizardens
// state-opdatering (ændring på Generelt) og herfra (regimeskift, manuelle til-/fravalg).

// ── Main component ────────────────────────────────────────────────────────────

export function SectionVilkaar({ state, onChange }: {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
}) {
  const [items, setItems] = useState<ClauseItem[]>(() => initItems(state))
  const [search, setSearch] = useState('')
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set())
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [textboxMode, setTextboxMode] = useState(false)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const [confirmEditId, setConfirmEditId] = useState<string | null>(null)
  const [smartOverrides, setSmartOverrides] = useState<Record<string, string>>(() => state.smart_overrides ?? {})

  // ── Saved templates (#15) ──────────────────────────────────────────────────
  const [saved, setSaved] = useState<SavedVilkaar[]>([])

  const loadSaved = async () => {
    try {
      const rows = await apiFetch<SavedVilkaar[]>('/api/v1/vilkaar')
      setSaved(rows)
    } catch {
      // Non-fatal — the panels just show the empty placeholder.
    }
  }

  // Genuine one-shot mount-time data load (fetches saved vilkår templates);
  // restructuring risks changing when the fetch fires.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { loadSaved() }, [])

  // Regimeskift: afstem de forvalgte klausuler (kun auto-tilføjede fjernes). special_terms
  // regenereres i tekstboks-tilstand kun, hvis landlord ikke har redigeret teksten frit.
  const changeRegime = (regime: RentRegime) => {
    const oldIds = items.filter(i => i.kind === 'smart').map(i => (i as { kind: 'smart'; id: string }).id)
    const r = reconcileRegime(oldIds, { freeRent: state.free_rent, reason: state.free_rent_reason, regime }, state.regime_applied)
    const next: ClauseItem[] = [
      ...items.filter(i => !(i.kind === 'smart' && !r.ids.includes((i as { kind: 'smart'; id: string }).id))),
      ...r.ids.filter(id => !oldIds.includes(id)).map(id => ({ kind: 'smart', id }) as ClauseItem),
    ]
    setItems(next)
    const meta = { rent_regime: r.regime, regime_applied: r.applied }
    const idsChanged = r.ids.join() !== oldIds.join()
    onChange(textboxMode
      ? {
          ...meta,
          smartvilkaar_active: r.ids,
          // Uredigeret tekst regenereres; fri redigering bevares men flagges til gennemgang.
          ...(idsChanged
            ? state.special_terms_edited
              ? { special_terms_review: true }
              : { special_terms: specialTermsFor(r.ids, state.special_terms_clauses, smartOverrides) }
            : {}),
        }
      : { ...toState(next, smartOverrides), ...meta })
  }

  const saveAsTemplate = async (scope: 'personal' | 'company', title: string, content: string) => {
    if (!content.trim()) return
    try {
      const row = await apiFetch<SavedVilkaar>('/api/v1/vilkaar', {
        method: 'POST',
        body: JSON.stringify({ scope, title: title || null, content }),
      })
      setSaved(prev => [row, ...prev])
    } catch {
      // ignore — keep UI responsive
    }
  }

  const insertSaved = (tpl: SavedVilkaar) =>
    // Only invoked from click handlers (onClick / onInsert callbacks fired on
    // user interaction), never during render; the compiler can't trace the
    // prop-passed callback as click-only.
    // eslint-disable-next-line react-hooks/purity
    update([...items, { kind: 'custom', localId: `tpl-${tpl.id}-${Date.now()}`, text: tpl.content }])

  const personalSaved = saved.filter(s => s.scope === 'personal')
  const companySaved = saved.filter(s => s.scope === 'company')

  const update = (next: ClauseItem[]) => {
    // Manuelt til-/fravalg af en klausul: opdatér regime-sporingen, så landlords valg
    // hverken overskrives eller genindsættes ved næste regimeskift.
    const smartOf = (list: ClauseItem[]) =>
      list.filter(i => i.kind === 'smart').map(i => (i as { kind: 'smart'; id: string }).id)
    const before = smartOf(items)
    const after = smartOf(next)
    let applied = state.regime_applied
    for (const id of before) if (!after.includes(id)) applied = noteManualChange(applied, id, false)
    for (const id of after) if (!before.includes(id)) applied = noteManualChange(applied, id, true)
    setItems(next)
    onChange(applied !== state.regime_applied
      ? { ...toState(next, smartOverrides), regime_applied: applied }
      : toState(next, smartOverrides))
  }

  const confirmEdit = () => {
    if (!confirmEditId) return
    const clause = SMARTVILKAAR.find(c => c.id === confirmEditId)
    if (clause) {
      setSmartOverrides(prev => {
        const next = { ...prev, [confirmEditId]: clause.text }
        onChange(toState(items, next))
        return next
      })
    }
    setConfirmEditId(null)
  }

  const updateSmartOverride = (id: string, text: string) => {
    const next = { ...smartOverrides, [id]: text }
    setSmartOverrides(next)
    onChange(toState(items, next))
  }

  const activeIds = new Set(
    items.filter(i => i.kind === 'smart').map(i => (i as { kind: 'smart'; id: string }).id)
  )

  const toggleSmart = (id: string) => {
    if (activeIds.has(id)) {
      update(items.filter(i => !(i.kind === 'smart' && (i as { kind: 'smart'; id: string }).id === id)))
    } else {
      update([...items, { kind: 'smart', id }])
    }
  }

  const moveUp = (idx: number) => {
    if (idx === 0) return
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
    update(next)
  }

  const removeItem = (idx: number) => update(items.filter((_, i) => i !== idx))

  const updateText = (localId: string, text: string) =>
    update(items.map(i =>
      i.kind === 'custom' && (i as { kind: 'custom'; localId: string; text: string }).localId === localId
        ? { ...i, text } : i
    ) as ClauseItem[])

  const addCustom = () =>
    // Only invoked from click handlers (onClick / EmptyState onAdd), never
    // during render.
    // eslint-disable-next-line react-hooks/purity
    update([...items, { kind: 'custom', localId: `c-${Date.now()}`, text: '' }])

  const toggleCat = (cat: string) =>
    setCollapsedCats(prev => {
      const next = new Set(prev)
      if (next.has(cat)) { next.delete(cat) } else { next.add(cat) }
      return next
    })

  const handleDrop = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return
    const next = [...items]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)
    update(next)
    setDragIdx(null)
    setDragOverIdx(null)
  }

  const filtered = search.trim()
    ? SMARTVILKAAR.filter(c =>
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        c.text.toLowerCase().includes(search.toLowerCase())
      )
    : SMARTVILKAAR

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* Confirmation modal */}
      {confirmEditId && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: '28px 24px',
            maxWidth: 420, width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
              Rediger vilkår
            </div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 24 }}>
              Er du sikker på at du vil redigere vilkåret? Standardteksten kopieres og kan herefter ændres frit.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmEditId(null)}
                style={{
                  fontSize: 14, padding: '9px 18px', borderRadius: 8,
                  background: 'white', border: '1px solid #e5e7eb', cursor: 'pointer', color: '#374151',
                }}
              >Annuller</button>
              <button
                onClick={confirmEdit}
                style={{
                  fontSize: 14, fontWeight: 600, padding: '9px 18px', borderRadius: 8,
                  background: BLUE, color: 'white', border: 'none', cursor: 'pointer',
                }}
              >Rediger vilkår</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Left: clause area ── */}
      <div style={{ flex: 1, minWidth: 0, borderRight: '1px solid #e9eaeb', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Sticky action bar */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '12px 24px',
          borderBottom: '1px solid #f0f0f0', background: 'white', gap: 8,
          position: 'sticky', top: 0, zIndex: 2,
        }}>
          <button
            onClick={addCustom}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 15,
              color: '#374151', background: 'white', border: '1px solid #d1d5db',
              borderRadius: 7, padding: '7px 14px', cursor: 'pointer',
            }}
          >+ Tilføj eget vilkår</button>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: textboxMode ? '#9ca3af' : '#0f172a', fontWeight: 500 }}>
              Smartvilkår
            </span>
            <div
              onClick={() => setTextboxMode(p => !p)}
              style={{
                width: 41, height: 23, borderRadius: 12, cursor: 'pointer', position: 'relative',
                background: textboxMode ? BLUE : '#d1d5db', transition: 'background .15s',
              }}
            >
              <div style={{
                position: 'absolute', top: 3, left: textboxMode ? 21 : 3,
                width: 17, height: 17, borderRadius: '50%', background: 'white',
                transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)',
              }} />
            </div>
            <span style={{ fontSize: 13, color: textboxMode ? '#0f172a' : '#9ca3af', fontWeight: 500 }}>
              Tekstboks
            </span>
          </div>
        </div>

        <div style={{ padding: '20px 24px 40px', flex: 1 }}>
          <RegimePanel
            state={state}
            activeIds={activeIds}
            onRegime={changeRegime}
            onToggle={toggleSmart}
          />

          {state.special_terms_review && (
            <div role="alert" style={{
              background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
              borderRadius: 8, padding: '14px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.6,
            }}>
              Du har ændret regime eller grundlag, efter at du selv redigerede § 11. Din tekst er bevaret, men den kan
              stadig indeholde en klausul, der ikke længere passer. Gennemgå § 11, før kontrakten kan sendes.
              <div>
                <button
                  onClick={() => onChange({ special_terms_review: false })}
                  style={{
                    marginTop: 10, background: 'white', color: '#92400e', border: '1px solid #fcd34d',
                    borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
                  }}
                >Jeg har gennemgået § 11</button>
              </div>
            </div>
          )}

          {/* Blue info banner */}
          {!bannerDismissed && (
            <div style={{
              background: '#f0f4ff', border: '1px solid #c7d2fe', color: '#3730a3',
              borderRadius: 8, padding: '14px 16px', marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Her kan du anføre eventuelle fravigelser og tilføjelser i forhold til lejelovens almindelige regler
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6, color: '#4338ca' }}>
                Hvis du har valgt noget undervejs i lejekontraktguiden, som kræver et særligt vilkår, har systemet
                allerede tilføjet dette helt automatisk. Hvis du vil tilføje flere særlige vilkår kan du både benytte
                vores Smartvilkår eller tilføje og formulere dine helt egne vilkår.
              </div>
              <button
                onClick={() => setBannerDismissed(true)}
                style={{
                  marginTop: 10, background: 'white', color: '#4f46e5',
                  border: '1px solid #c7d2fe', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
                }}
              >Forstået</button>
            </div>
          )}

          {textboxMode ? (
            <textarea
              style={{ ...inputStyle, minHeight: 400, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.7 }}
              value={state.special_terms_edited ? state.special_terms : (state.special_terms || computeSpecialTerms(items, smartOverrides))}
              onChange={e => onChange({ special_terms: e.target.value, special_terms_edited: true })}
              placeholder="Skriv dine særlige vilkår her..."
            />
          ) : items.length === 0 ? (
            <EmptyState onAdd={addCustom} />
          ) : (
            <div data-testid="vilkaar-items" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {items.map((item, idx) => {
                const isDragging = dragIdx === idx
                const isDragOver = dragOverIdx === idx && dragIdx !== idx
                const onDragHandleStart = (e: DragEvent) => {
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', String(idx))
                  setDragIdx(idx)
                }
                const onCardDragOver = (e: DragEvent) => {
                  e.preventDefault()
                  if (dragIdx !== null) setDragOverIdx(idx)
                }
                const onCardDrop = (e: DragEvent) => {
                  e.preventDefault()
                  if (dragIdx !== null) handleDrop(dragIdx, idx)
                }
                const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null) }

                if (item.kind === 'smart') {
                  const id = (item as { kind: 'smart'; id: string }).id
                  const clause = SMARTVILKAAR.find(c => c.id === id)!
                  return (
                    <SmartCard
                      key={id}
                      number={idx + 1}
                      clause={clause}
                      canMoveUp={idx > 0}
                      onMoveUp={() => moveUp(idx)}
                      onRemove={() => removeItem(idx)}
                      isDragging={isDragging}
                      isDragOver={isDragOver}
                      onDragHandleStart={onDragHandleStart}
                      onDragOver={onCardDragOver}
                      onDrop={onCardDrop}
                      onDragEnd={onDragEnd}
                      editedText={smartOverrides[id]}
                      onClickBody={() => {
                        if (smartOverrides[id] === undefined) setConfirmEditId(id)
                      }}
                      onEditText={t => updateSmartOverride(id, t)}
                      onSave={scope => saveAsTemplate(scope, clause.title, smartOverrides[id] ?? clause.text)}
                    />
                  )
                }
                const ci = item as { kind: 'custom'; localId: string; text: string }
                return (
                  <CustomCard
                    key={ci.localId}
                    number={idx + 1}
                    text={ci.text}
                    canMoveUp={idx > 0}
                    onMoveUp={() => moveUp(idx)}
                    onRemove={() => removeItem(idx)}
                    onChange={t => updateText(ci.localId, t)}
                    isDragging={isDragging}
                    isDragOver={isDragOver}
                    onDragHandleStart={onDragHandleStart}
                    onDragOver={onCardDragOver}
                    onDrop={onCardDrop}
                    onDragEnd={onDragEnd}
                    onSave={scope => saveAsTemplate(scope, '', ci.text)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Right: Smartvilkår panel (independent scroll — #16) ── */}
      <div style={{
        width: 280, flexShrink: 0, background: '#f7f8fa', borderLeft: '1px solid #e8eaed',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 56px)', overflowY: 'auto',
      }}>
        {/* Sticky header */}
        <div style={{
          position: 'sticky', top: 0, background: '#f7f8fa', zIndex: 1,
          borderBottom: '1px solid #e8eaed', padding: '14px 14px 10px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>Smartvilkår</div>
          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5, marginBottom: 10 }}>
            Vælg mellem standardvilkår, udformet af advokater.
          </div>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingLeft: 34, fontSize: 13 }}
              placeholder="Søg efter vilkår..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <svg
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
        </div>

        {/* Saved templates (#15) */}
        <SavedPanel
          title="Dine Smartvilkår"
          empty="Der er ikke nogle gemte Smartvilkår endnu."
          items={personalSaved}
          onInsert={insertSaved}
        />
        <SavedPanel
          title="Selskabsvilkår"
          empty="Der er ikke nogle gemte selskabsvilkår endnu."
          items={companySaved}
          onInsert={insertSaved}
        />

        <div style={{ padding: '12px 16px 24px', flex: 1 }}>
          {CATEGORIES.map(cat => {
            const catItems = filtered.filter(c => c.category === cat)
            if (!catItems.length) return null
            const collapsed = collapsedCats.has(cat)
            return (
              <div key={cat} style={{ marginBottom: 8 }}>
                <button
                  onClick={() => toggleCat(cat)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', padding: '8px 10px', background: 'white',
                    border: '1px solid #e8eaed', borderRadius: collapsed ? 7 : '7px 7px 0 0',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151',
                  }}
                >
                  <span>{cat}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {!collapsed && (
                  <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 7px 7px', overflow: 'hidden' }}>
                    {catItems.map((clause, i) => {
                      const active = activeIds.has(clause.id)
                      return (
                        <div
                          key={clause.id}
                          onClick={() => toggleSmart(clause.id)}
                          style={{
                            display: 'flex', gap: 10, padding: '10px 12px', cursor: 'pointer',
                            background: active ? '#eff6ff' : 'white',
                            borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                          }}
                        >
                          <div style={{
                            width: 17, height: 17, borderRadius: 3, flexShrink: 0, marginTop: 2,
                            border: `2px solid ${active ? BLUE : '#d1d5db'}`,
                            background: active ? BLUE : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {active && (
                              <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
                                <polyline points="2 6 5 9 10 3"/>
                              </svg>
                            )}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: active ? '#1e40af' : '#374151', marginBottom: 2, lineHeight: 1.3 }}>
                              {clause.title}
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.4 }}>
                              {clause.text.slice(0, 80)}{clause.text.length > 80 ? '…' : ''}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Regime-vælger + forslags-boks. Neutral: anbefaler aldrig et regime.
function RegimePanel({ state, activeIds, onRegime, onToggle }: {
  state: WizardState
  activeIds: Set<string>
  onRegime: (r: RentRegime) => void
  onToggle: (id: string) => void
}) {
  const regime: RentRegime = state.free_rent ? (state.rent_regime ?? '') : ''
  const res = regimeClauses(state.free_rent, state.free_rent_reason, regime)
  const grundlagKnown = state.free_rent_reason in GRUNDLAG_LABEL
  const pickerEnabled = state.free_rent && grundlagKnown
  const options: { value: RentRegime; label: string }[] = [
    { value: '', label: 'Ikke afklaret' },
    { value: 'ureguleret', label: 'Ureguleret' },
    { value: 'reguleret', label: 'Reguleret' },
  ]
  const titleOf = (id: string) => SMARTVILKAAR.find(c => c.id === id)?.title ?? id
  return (
    <div data-testid="regime-panel" style={{
      border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 16, background: 'white',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Reguleringsregime for lejen</div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 10 }}>
        Angiv hvilket regime der er aftalt for lejemålet. Vælg selv; systemet anbefaler ikke et regime.
        Ureguleret: fravalg af lejelovens §§ 42-45. Reguleret: fravalg af lejelovens § 41, stk. 2.
      </div>
      {pickerEnabled && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {options.map(o => {
          const sel = regime === o.value
          return (
            <button
              key={o.value || 'none'}
              type="button"
              aria-pressed={sel}
              onClick={() => onRegime(o.value)}
              style={{
                fontSize: 13, padding: '7px 14px', borderRadius: 7, cursor: 'pointer',
                border: `1px solid ${sel ? BLUE : '#d1d5db'}`,
                background: sel ? '#eef2ff' : 'white', color: sel ? BLUE : '#374151',
                fontWeight: sel ? 600 : 500,
              }}
            >{o.label}</button>
          )
        })}
      </div>}

      {res.status === 'uafklaret' ? (
        <div data-testid="regime-uafklaret" style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
          {!state.free_rent
            ? 'Fri leje er ikke valgt på Generelt-trinnet – ingen klausul foreslået.'
            : !grundlagKnown
              ? 'Egen begrundelse – vælg klausul manuelt.'
              : 'Regimet er ikke afklaret – ingen klausul er forvalgt.'}
        </div>
      ) : (
        <div data-testid="regime-forslag" style={{
          background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '12px 14px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#3730a3', marginBottom: 6 }}>
            Foreslået klausul ud fra regime
          </div>
          <div style={{ fontSize: 12, color: '#4338ca', lineHeight: 1.6, marginBottom: 8 }}>
            Grundlag: {GRUNDLAG_LABEL[state.free_rent_reason]}<br />
            Regime: {REGIME_LABEL[regime as 'ureguleret' | 'reguleret']}
          </div>
          {res.ids.map(id => {
            const on = activeIds.has(id)
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{titleOf(id)}</span>
                <button
                  type="button"
                  onClick={() => onToggle(id)}
                  style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                    background: 'white', border: '1px solid #c7d2fe', color: BLUE, fontWeight: 500,
                  }}
                >{on ? 'Fravælg' : 'Vælg klausul'}</button>
                {!on && <span style={{ fontSize: 12, color: '#6b7280' }}>Fravalgt</span>}
              </div>
            )
          })}
          {res.alternative && res.alternative.length > 0 && (
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginTop: 8 }}>
              Alternativ: Er lejemålet i stedet en nypåbygget etage (§ 54, stk. 1, nr. 3), kan du manuelt
              vælge «{res.alternative.map(titleOf).join(', ')}» under Smartvilkår og fravælge forslaget ovenfor.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function HighlightedText({ text }: { text: string }) {
  const parts = text.split(/(\[.*?\])/)
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {parts.map((part, i) =>
        /^\[.*\]$/.test(part)
          ? <span key={i} style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 3, padding: '0 2px', fontWeight: 500 }}>{part}</span>
          : part
      )}
    </span>
  )
}

function DragHandle() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="currentColor">
      <circle cx="3.5" cy="2.5" r="1.4"/>
      <circle cx="8.5" cy="2.5" r="1.4"/>
      <circle cx="3.5" cy="7" r="1.4"/>
      <circle cx="8.5" cy="7" r="1.4"/>
      <circle cx="3.5" cy="11.5" r="1.4"/>
      <circle cx="8.5" cy="11.5" r="1.4"/>
    </svg>
  )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
      <div style={{ marginBottom: 16, opacity: 0.3 }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      </div>
      <div style={{ fontSize: 14, marginBottom: 16 }}>Ingen vilkår tilføjet endnu</div>
      <button
        onClick={onAdd}
        style={{
          fontSize: 15, color: BLUE, background: 'none',
          border: `1px solid ${BLUE}`, borderRadius: 7, padding: '8px 18px', cursor: 'pointer',
        }}
      >+ Tilføj eget vilkår</button>
    </div>
  )
}

// Sidebar panel listing saved reusable templates (#15). Click a row to insert it.
function SavedPanel({ title, empty, items, onInsert }: {
  title: string
  empty: string
  items: SavedVilkaar[]
  onInsert: (tpl: SavedVilkaar) => void
}) {
  return (
    <div style={{ padding: '12px 16px 0' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 8 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5, marginBottom: 8 }}>{empty}</div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 7, overflow: 'hidden', marginBottom: 8 }}>
          {items.map((tpl, i) => (
            <div
              key={tpl.id}
              onClick={() => onInsert(tpl)}
              title="Klik for at indsætte som vilkår"
              style={{
                padding: '10px 12px', cursor: 'pointer', background: 'white',
                borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 2, lineHeight: 1.3 }}>
                {tpl.title || 'Eget vilkår'}
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.4 }}>
                {tpl.content.slice(0, 80)}{tpl.content.length > 80 ? '…' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SmartCard({ number, clause, canMoveUp, onMoveUp, onRemove, isDragging, isDragOver,
  onDragHandleStart, onDragOver, onDrop, onDragEnd, editedText, onClickBody, onEditText, onSave }: {
  number: number
  clause: SmartClause
  canMoveUp: boolean
  onMoveUp: () => void
  onRemove: () => void
  isDragging: boolean
  isDragOver: boolean
  onDragHandleStart: (e: DragEvent) => void
  onDragOver: (e: DragEvent) => void
  onDrop: (e: DragEvent) => void
  onDragEnd: () => void
  editedText: string | undefined
  onClickBody: () => void
  onEditText: (t: string) => void
  onSave: (scope: 'personal' | 'company') => void
}) {
  const displayText = editedText ?? clause.text
  const hasPlaceholder = /\[.*?\]/.test(displayText)
  const isEditing = editedText !== undefined
  const [inlineEditing, setInlineEditing] = useState(false)

  const wasEditing = useRef(isEditing)
  useEffect(() => {
    if (!wasEditing.current && isEditing) setInlineEditing(true)
    wasEditing.current = isEditing
  }, [isEditing])

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        border: isDragOver ? `2px dashed ${BLUE}` : '1px solid #e8eaed',
        borderRadius: 8, background: 'white', overflow: 'hidden',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity .15s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '10px 14px', borderBottom: '1px solid #f3f4f6', gap: 10,
        background: '#fafafa',
      }}>
        <div
          draggable
          onDragStart={onDragHandleStart}
          onDragEnd={onDragEnd}
          style={{ cursor: 'grab', flexShrink: 0, color: '#9ca3af', display: 'flex', padding: 2 }}
        >
          <DragHandle />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginBottom: 2 }}>§11, stk. {number}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{clause.title}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {hasPlaceholder && (
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap',
              background: '#fee2e2', color: '#dc2626', fontWeight: 500,
            }}>Mangler input</span>
          )}
          <CardBtn disabled={!canMoveUp} onClick={onMoveUp}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="18 15 12 9 6 15"/>
            </svg>
          </CardBtn>
          <CardBtn onClick={onRemove} danger>
            <TrashIcon />
          </CardBtn>
          <SaveMenu onSave={onSave} />
        </div>
      </div>
      {inlineEditing ? (
        <div style={{ padding: '12px 16px' }}>
          <textarea
            style={{ ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.75 }}
            value={displayText}
            onChange={e => onEditText(e.target.value)}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={() => setInlineEditing(false)}
              style={{
                fontSize: 13, fontWeight: 600, padding: '8px 18px', borderRadius: 8,
                background: BLUE, color: 'white', border: 'none', cursor: 'pointer',
              }}
            >Gem ændringer</button>
          </div>
        </div>
      ) : (
        <div
          onClick={isEditing ? () => setInlineEditing(true) : onClickBody}
          title="Klik for at redigere"
          style={{ padding: '14px 16px', fontSize: 13, color: '#374151', lineHeight: 1.75, cursor: 'text' }}
        >
          <HighlightedText text={displayText} />
        </div>
      )}
    </div>
  )
}

function CustomCard({ number, text, canMoveUp, onMoveUp, onRemove, onChange, isDragging, isDragOver,
  onDragHandleStart, onDragOver, onDrop, onDragEnd, onSave }: {
  number: number
  text: string
  canMoveUp: boolean
  onMoveUp: () => void
  onRemove: () => void
  onChange: (t: string) => void
  isDragging: boolean
  isDragOver: boolean
  onDragHandleStart: (e: DragEvent) => void
  onDragOver: (e: DragEvent) => void
  onDrop: (e: DragEvent) => void
  onDragEnd: () => void
  onSave: (scope: 'personal' | 'company') => void
}) {
  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        border: isDragOver ? `2px dashed ${BLUE}` : '1px solid #e8eaed',
        borderRadius: 8, background: 'white', overflow: 'hidden',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity .15s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '10px 14px', borderBottom: '1px solid #f3f4f6', gap: 10,
        background: '#fafafa',
      }}>
        <div
          draggable
          onDragStart={onDragHandleStart}
          onDragEnd={onDragEnd}
          style={{ cursor: 'grab', flexShrink: 0, color: '#9ca3af', display: 'flex', padding: 2 }}
        >
          <DragHandle />
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>§11, stk. {number} — Eget vilkår</div>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', flexShrink: 0 }}>
          <CardBtn disabled={!canMoveUp} onClick={onMoveUp}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="18 15 12 9 6 15"/>
            </svg>
          </CardBtn>
          <CardBtn onClick={onRemove} danger>
            <TrashIcon />
          </CardBtn>
          <SaveMenu onSave={onSave} />
        </div>
      </div>
      <div style={{ padding: '12px 16px' }}>
        <textarea
          style={{ ...inputStyle, minHeight: 115, resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
          value={text}
          onChange={e => onChange(e.target.value)}
          placeholder="Skriv dit særlige vilkår her..."
        />
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>{text.length} tegn</div>
      </div>
    </div>
  )
}

function CardBtn({ children, onClick, disabled, danger }: {
  children: ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #e5e7eb', borderRadius: 6,
        cursor: disabled ? 'default' : 'pointer',
        background: 'white',
        color: disabled ? '#e5e7eb' : danger ? '#dc2626' : '#6b7280',
      }}
    >
      {children}
    </button>
  )
}

// ⋮ menu — save this clause as a reusable template (#15)
function SaveMenu({ onSave }: { onSave: (scope: 'personal' | 'company') => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Gem som vilkår"
        style={{
          width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer',
          background: 'white', color: '#6b7280',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
        </svg>
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          style={{
            position: 'absolute', top: '110%', right: 0, zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,.1)', minWidth: 210, overflow: 'hidden',
          }}
        >
          <button
            onClick={() => { onSave('personal'); setOpen(false) }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
              fontSize: 13, color: '#374151', background: 'none', border: 'none',
              cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
            }}
          >Tilføj til dine Smartvilkår</button>
          <button
            onClick={() => { onSave('company'); setOpen(false) }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
              fontSize: 13, color: '#374151', background: 'none', border: 'none', cursor: 'pointer',
            }}
          >Tilføj til selskabsvilkår</button>
        </div>
      )}
    </div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14H6L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  )
}
